import React,{ forwardRef } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    IconButton,
    ButtonGroup,
    Typography,
    LinearProgress,
    Alert,
    Link as MuiLink,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronCircleLeft, faChevronCircleRight } from '@fortawesome/free-solid-svg-icons';
import { DossierData, TemplateOptions, CalendarEvent, Kedo } from '../types';
import {
    useCalendarData,
    formatDayName,
    formatDayNumber,
    formatMonthYear,
} from '../../../hooks';
import { LoadingState } from '../base';

interface EventCalendarBlockProps {
    kedo: Kedo;
    data: DossierData | null;
    templateOptions: TemplateOptions | null;
    loading?: boolean;
}

export const EventCalendarBlock = forwardRef<HTMLDivElement, EventCalendarBlockProps>(
    ({ kedo, data, templateOptions, loading }, ref) => {
        const {
            events,
            locations,
            currentDay,
            nextDay,
            dayAfterNext,
            incrementDay,
            decrementDay,
            getEventsForLocation,
            isEventOnDay,
        } = useCalendarData({ data, templateOptions });

        if (
            !templateOptions ||
            templateOptions.name === null ||
            templateOptions.location === null ||
            templateOptions.start === null ||
            templateOptions.end === null
        ) {
            return (
                <Alert severity="warning">
                    {kedo.t('Event module not properly setup. Please guide your contract admin.')}
                </Alert>
            );
        }

        if (loading) {
            return <LoadingState />;
        }

        const days = [currentDay, nextDay, dayAfterNext];

        const renderEventCell = (
            locationEvents: CalendarEvent[],
            day: Date
        ) => {
            return (
                <TableCell key={day.toISOString()}>
                    {locationEvents.map((event) => {
                        if (isEventOnDay(event, day, 'start')) {
                            return (
                                <MuiLink
                                    key={`start-${event.id}`}
                                    component={Link}
                                    to={`/contentdossier/${event.id}`}
                                    underline="none"
                                >
                                    <Box sx={{ mb: 0.5 }}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={100}
                                            color="success"
                                            sx={{
                                                height: 24,
                                                borderRadius: 1,
                                                '& .MuiLinearProgress-bar': {
                                                    borderRadius: 1,
                                                },
                                            }}
                                        />
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                position: 'relative',
                                                top: -20,
                                                left: 8,
                                                color: 'white',
                                            }}
                                        >
                                            {event.name}
                                        </Typography>
                                    </Box>
                                </MuiLink>
                            );
                        }

                        if (isEventOnDay(event, day, 'end')) {
                            return (
                                <MuiLink
                                    key={`end-${event.id}`}
                                    component={Link}
                                    to={`/contentdossier/${event.id}`}
                                    underline="none"
                                >
                                    <Box sx={{ mb: 0.5 }}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={100}
                                            color="error"
                                            sx={{
                                                height: 24,
                                                borderRadius: 1,
                                                '& .MuiLinearProgress-bar': {
                                                    borderRadius: 1,
                                                },
                                            }}
                                        />
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                position: 'relative',
                                                top: -20,
                                                left: 8,
                                                color: 'white',
                                            }}
                                        >
                                            {event.name}
                                        </Typography>
                                    </Box>
                                </MuiLink>
                            );
                        }

                        return null;
                    })}
                </TableCell>
            );
        };

        return (
            <Box ref={ref} id="eventcalendar">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Typography variant="subtitle1">
                                        {formatMonthYear(currentDay)}
                                    </Typography>
                                    <ButtonGroup size="small">
                                        <IconButton onClick={decrementDay} size="small">

                                            <FontAwesomeIcon icon={faChevronCircleLeft} />
                                        </IconButton>
                                        <IconButton onClick={incrementDay} size="small">
                                            <FontAwesomeIcon icon={faChevronCircleRight} />
                                        </IconButton>
                                    </ButtonGroup>
                                </Box>
                            </TableCell>
                            {days.map((day) => (
                                <TableCell key={day.toISOString()} align="center">
                                    <Typography variant="body2" color="text.secondary">
                                        {formatDayName(day)}
                                    </Typography>
                                    <Typography variant="h6">
                                        {formatDayNumber(day)}
                                    </Typography>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {locations.map((location) => {
                            const locationEvents = getEventsForLocation(location.name);

                            return (
                                <TableRow key={location.id}>
                                    <TableCell>
                                        <MuiLink
                                            component={Link}
                                            to={`/contentdossier/${location.id}`}
                                        >
                                            {location.name}
                                        </MuiLink>
                                    </TableCell>
                                    {days.map((day) => renderEventCell(locationEvents, day))}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </Box>
        );
    }
);

EventCalendarBlock.displayName = 'EventCalendarBlock';
